import { useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { firstProblem, minLen } from "../../components/validators";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { EngineOverviewDto, CustomerAction } from "../../ipc/types";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): CustomerAction[] {
  if (statusLabel === "Activo") return ["deactivate", "archive"];
  if (statusLabel === "Borrador" || statusLabel === "Inactivo") return ["activate", "archive"];
  return [];
}
const ACTION_LABEL: Record<CustomerAction, string> = { activate: "Activar", deactivate: "Desactivar", archive: "Archivar" };

function NewCustomerForm({ onCreate, onCancel, busy }: { onCreate: (v: { code: string; name: string; type: string }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("retail");
  const hint = firstProblem([
    { ok: minLen(code, 2), msg: "Ingresa un código (mínimo 2 caracteres)" },
    { ok: minLen(name, 2), msg: "Ingresa un nombre" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nuevo cliente" subtitle="Se crea en estado Borrador; luego actívalo cuando esté listo" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">Código</span>
            <input className="pd-input" value={code} placeholder="Ej: CLI-006" aria-label="Código" onChange={(e) => setCode(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Tipo</span>
            <select className="pd-input" value={type} aria-label="Tipo" onChange={(e) => setType(e.target.value)}>
              <option value="retail">Minorista</option>
              <option value="corporate">Corporativo</option>
            </select></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Nombre</span>
            <input className="pd-input" value={name} placeholder="Ej: Camila Rojas" aria-label="Nombre" onChange={(e) => setName(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ code: code.trim(), name: name.trim(), type })}>
            {busy ? "Creando…" : "Crear cliente"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function CustomerScreen(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("customer"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { code: string; name: string; type: string }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createCustomer(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Cliente "${v.name}" creado.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: CustomerAction): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.setCustomerStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="customer" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Clientes</h1>
              <p className="pagehead__sub">Ficha maestra de clientes. Crea y gestiona; se guardan en tu equipo.</p>
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics }} />
            <Button size="sm" icon="customer" onClick={() => setShowForm((s) => !s)}>Nuevo cliente</Button>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewCustomerForm onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Cartera" subtitle={`${data.rows.length} clientes`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="customer" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes clientes</div><div className="faint" style={{ fontSize: 13 }}>Crea el primero con “Nuevo cliente”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Cliente</th><th>Detalle</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "archive" ? "ghost" : "secondary"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
